import { useCallback, useEffect, useReducer } from "react";

import type { Campaign } from "../api/generated";
import type { LanguageFilterChipOption } from "../LanguageFilterChip";
import type { LanguageSelectOption } from "../LanguageSelect";
import type { SelectV3Option } from "../SelectV3";
import type {
  CreateLinkCampaignConfig,
  CreateLinkTranslations,
  LandingPageLink,
  OfferLandingPages,
  ReferralLinkModalsActions,
  ReferralLinkModalsAnalytics,
  TargetPageTranslations,
} from "./types";

import { Button as ButtonV2 } from "../Button";
import { CampaignPicker } from "../Campaign/CampaignPicker";
import {
  PENDING_CAMPAIGN_ID,
  usePendingCampaign,
} from "../Campaign/hooks/usePendingCampaign";
import { FieldErrorV2 } from "../FieldErrorV2";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  AlertTriageIcon,
  ChevronRightIcon,
  ClockBadgeIcon,
  ExternalLinkIcon,
  InfoIcon,
} from "../Icons";
import { InputV2 } from "../InputV2";
import { LanguageSelect } from "../LanguageSelect";
import { ModalFooterSkeleton } from "../ModalSkeleton";
import { ResponsiveModal } from "../ResponsiveModal";
import { SkeletonLoader } from "../SkeletonLoader";
import { TextButton } from "../TextButton";
import { showSuccessToast } from "../Toast";
import { Typo } from "../Typo";
import { useDebounceFn } from "../utils/debounce";
import { RebateProgramSelect } from "./RebateProgramSelect";
import { createInitialState, modalReducer } from "./reducer";
import { TargetPageModal } from "./TargetPageModal";
import { useCreateReferralLinkData } from "./useCreateReferralLinkData";

type CreateLinkModalProps = {
  isOpen: boolean;
  onClose: (refresh?: boolean) => void;
  actions: ReferralLinkModalsActions;
  translations: CreateLinkTranslations;
  targetPageTranslations: TargetPageTranslations;
  getLanguageLabelFromCode: (code: string) => string;
  analytics?: ReferralLinkModalsAnalytics;
  campaign?: CreateLinkCampaignConfig;
};

const LINK_NAME_MAX_LENGTH = 60;

const FieldError = ({ text }: { text: string }) => (
  <div
    className="rounded-lg bg-background-failed p-3"
    data-testid="create-link-error"
  >
    <FieldErrorV2 text={text} />
  </div>
);

const FieldWarning = ({
  text,
  viewLinkText,
  handleClose,
}: {
  text: string;
  viewLinkText: string;
  handleClose: () => void;
}) => (
  <div
    className="flex items-center justify-between gap-4 rounded-lg bg-background-warning p-4"
    data-testid="create-link-duplicate"
  >
    <div className="flex items-start gap-2">
      <InfoIcon className="h-4 w-4 shrink-0 [&_path]:stroke-text-warning-boldest" />
      <Typo type="bodySM" textColor="text-text-warning-boldest">
        {text}
      </Typo>
    </div>
    <TextButton
      onClick={handleClose}
      underlined
      size="xs"
      className="shrink-0 whitespace-nowrap"
    >
      {viewLinkText}
    </TextButton>
  </div>
);

type TargetPageSelectorProps = {
  offer: OfferLandingPages;
  landingPage: LandingPageLink;
  selectedLanguage: string;
  languageOptions: LanguageFilterChipOption[];
  onLanguageSelect: (option: LanguageSelectOption) => void;
  onOpenModal: () => void;
  translations: {
    targetPageLabel: string;
    targetPageSubLabel: string;
    previewLabel: string;
  };
};

const TargetPageSelector = ({
  offer,
  landingPage,
  selectedLanguage,
  languageOptions,
  onLanguageSelect,
  onOpenModal,
  translations,
}: TargetPageSelectorProps) => (
  <div className="flex flex-col gap-2" data-testid="target-page-section">
    <div className="flex flex-col gap-1">
      <Typo element="p" type="labelSM">
        {translations.targetPageLabel}
      </Typo>
      <Typo type="bodyXS" textColor="text-text-secondary">
        {translations.targetPageSubLabel}
      </Typo>
    </div>
    <div
      className="flex cursor-pointer items-center gap-4 rounded-lg bg-surface-secondary p-4 hover:bg-surface-tonal-default"
      data-testid="target-page-selector"
      role="button"
      tabIndex={0}
      onClick={onOpenModal}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenModal();
        }
      }}
    >
      {offer.thumbnailUrl ? (
        <img
          src={offer.thumbnailUrl}
          alt={offer.name}
          className="h-16 w-24 rounded-lg object-cover"
        />
      ) : (
        <div className="flex h-16 w-24 items-center justify-center rounded-lg bg-surface-tonal-default">
          <Typo type="bodyXS" textColor="text-text-secondary">
            {translations.previewLabel}
          </Typo>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1 text-text-brand-default">
        <Typo type="labelSM" smType="labelXS" className="w-full truncate">
          {landingPage.name}
        </Typo>
        <Typo
          type="bodyXS"
          textColor="text-text-tertiary"
          className="block w-full truncate"
        >
          {landingPage.previewUrl ?? ""}
        </Typo>
        {languageOptions.length > 0 && (
          <div className="mt-1" onClick={(e) => e.stopPropagation()}>
            <LanguageSelect
              options={languageOptions}
              value={selectedLanguage}
              onChange={onLanguageSelect}
              dropUp
              modal
              data-testid="language-dropdown"
            />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenModal();
        }}
        className="shrink-0 rounded-md p-1 transition-colors hover:bg-surface-tonal-default"
        data-testid="target-page-navigate"
        aria-label={translations.targetPageLabel}
      >
        <ChevronRightIcon className="h-4 w-4 [&_path]:stroke-icon-secondary" />
      </button>
    </div>
  </div>
);

const SkeletonLabel = ({ width }: { width: number }) => (
  <SkeletonLoader height={20} width={width} className="rounded" />
);

const TargetPageSelectorSkeleton = ({
  children,
}: {
  children?: React.ReactNode;
}) => (
  <div
    className="flex flex-col gap-2"
    data-testid="target-page-selector-skeleton"
  >
    {children}
    <SkeletonLoader height={109} className="rounded-2xl" />
  </div>
);

const CreateLinkFormSkeleton = ({
  showCampaign = false,
}: {
  showCampaign?: boolean;
}) => (
  <div className="flex flex-col gap-6" data-testid="create-link-form-skeleton">
    {/* Rebate program: label + input + sublabel */}
    <div className="flex flex-col gap-2">
      <SkeletonLabel width={88} />
      <SkeletonLoader height={40} className="rounded-lg" />
      <SkeletonLabel width={256} />
    </div>
    {/* Target page: label + sublabel + card */}
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <SkeletonLabel width={88} />
        <SkeletonLabel width={256} />
      </div>
      <SkeletonLoader height={109} className="rounded-2xl" />
    </div>
    {showCampaign && (
      <div
        className="flex flex-col gap-2"
        data-testid="campaign-field-skeleton"
      >
        <div className="flex flex-col gap-1">
          <SkeletonLabel width={88} />
          <SkeletonLabel width={256} />
        </div>
        <SkeletonLoader height={40} className="rounded-lg" />
      </div>
    )}
    {/* Link name: label + sublabel + input */}
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <SkeletonLabel width={88} />
        <SkeletonLabel width={256} />
      </div>
      <SkeletonLoader height={40} className="rounded-lg" />
    </div>
  </div>
);

const LoadErrorState = ({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry: () => void;
}) => (
  <div
    className="flex flex-col items-center justify-center gap-4 py-8"
    data-testid="create-link-load-error"
  >
    <AlertTriageIcon />
    <Typo type="bodySM" textColor="text-text-secondary" className="text-center">
      {message}
    </Typo>
    <ButtonV2 variant="outline" onClick={onRetry}>
      {retryLabel}
    </ButtonV2>
  </div>
);

const NoRebateProgramState = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => (
  <div
    className="flex flex-col items-center justify-center gap-4 py-8"
    data-testid="create-link-no-rebate-program"
  >
    <ClockBadgeIcon className="h-[52px] w-[52px]" />
    <div className="flex flex-col gap-2 text-center">
      <Typo type="labelSM">{title}</Typo>
      <Typo type="bodySM" textColor="text-text-secondary">
        {message}
      </Typo>
    </div>
  </div>
);

export const CreateLinkModal = ({
  isOpen,
  onClose,
  actions,
  translations,
  targetPageTranslations,
  getLanguageLabelFromCode,
  analytics,
  campaign,
}: CreateLinkModalProps) => {
  const isMobile = useIsMobile();

  const [trackLinkNameInputDebounced] = useDebounceFn(
    (value: string) => analytics?.trackCreateLinkNameEvent?.(value),
    1000,
  );

  const [state, dispatch] = useReducer(
    modalReducer,
    undefined,
    createInitialState,
  );

  const {
    selectedRebateProgram,
    selectedOffer,
    selectedLandingPage,
    linkName,
    isTargetPageModalOpen,
    selectedLanguage,
    selectedCampaign,
    status,
    nameFieldStatus,
  } = state;

  const {
    isGettingOfferIds,
    existingReferralLinks,
    isGettingReferralLinks,
    refetchReferralLinks,
    landingPageOffers,
    isGettingLandingPage,
    rebateProgramOptions,
    defaultRebateProgram,
    hasError,
    refetchInitialData,
  } = useCreateReferralLinkData({
    isOpen,
    selectedHOID: selectedRebateProgram?.hoid,
    actions,
  });

  const pendingCampaign = usePendingCampaign({
    campaigns: campaign?.campaigns ?? [],
    selected: selectedCampaign,
    onCreateCampaign: campaign?.onCreateCampaign,
    onCreateSuccess: campaign?.onCreateSuccess,
    createError: campaign?.createError,
  });

  const showRebateProgram = rebateProgramOptions.length > 1;

  const fallbackOffer = (landingPageOffers ?? [])[0];
  const currentOffer = selectedOffer ?? fallbackOffer;

  const checkIfDuplicate = useCallback(
    (offerId: string, landingPageId?: string, languageCode?: string) => {
      if (!selectedRebateProgram) return false;
      if (selectedCampaign?.id === PENDING_CAMPAIGN_ID) return false;
      const offerIdNum = Number(offerId);
      const landingPageIdNum = landingPageId ? Number(landingPageId) : 0;
      // Mirrors the API, which stores the partner's default campaign when none is chosen.
      const effectiveCampaignId =
        selectedCampaign?.id ??
        campaign?.campaigns.find((c) => c.isDefault)?.id ??
        null;

      return (existingReferralLinks ?? []).some(
        (link) =>
          link.tuneOfferId === offerIdNum &&
          link.tuneLandingPageId === landingPageIdNum &&
          link.languageCode === languageCode &&
          selectedRebateProgram.hoid === link.rebateProgram &&
          (!campaign || (link.campaignId ?? null) === effectiveCampaignId),
      );
    },
    [existingReferralLinks, selectedRebateProgram, selectedCampaign, campaign],
  );

  // Auto-select the first rebate program once options are available.
  useEffect(() => {
    if (selectedRebateProgram) return;
    const firstRebateProgram = rebateProgramOptions[0];
    if (!firstRebateProgram) return;
    dispatch({ type: "SELECT_REBATE_PROGRAM", program: firstRebateProgram });
  }, [selectedRebateProgram, rebateProgramOptions]);

  // Seed selectedOffer and selectedLandingPage once offers load.
  useEffect(() => {
    if (isGettingOfferIds || isGettingReferralLinks) return;
    if (selectedOffer && selectedLandingPage) return;
    if (!currentOffer) return;
    const pages = currentOffer.landingPages ?? [];
    const defaultPage = pages.find((p) => p.languageCode === "en") ?? pages[0];
    if (!defaultPage) return;

    dispatch({
      type: "INIT_TARGET_PAGE",
      offer: currentOffer,
      landingPage: defaultPage,
    });
  }, [
    currentOffer,
    selectedOffer,
    selectedLandingPage,
    isGettingOfferIds,
    isGettingReferralLinks,
  ]);

  // Whether the entered link name matches an existing referral link. Evaluated
  // on demand when the user clicks "Create link" (see handleCreateLink) rather
  // than proactively, so the duplicate-name error is not shown upfront.
  const isDuplicateLinkName = (name: string) => {
    const trimmed = name.trim();
    if (trimmed === "") return false;
    return existingReferralLinks.some(
      (link) =>
        Boolean(link.linkName?.trim()) && link.linkName?.trim() === trimmed,
    );
  };

  const codes = (selectedOffer?.landingPages ?? []).map((p) => p.languageCode);

  const languageOptions: LanguageFilterChipOption[] = codes.map((code) => ({
    value: code,
    label: getLanguageLabelFromCode(code),
  }));

  const handleRebateProgramChange = (option: SelectV3Option) => {
    analytics?.trackCreateLinkRebateProgramDropdownEvent?.(option.label);
    dispatch({
      type: "SELECT_REBATE_PROGRAM",
      program: rebateProgramOptions.find((o) => o.value === option.value),
    });
  };

  const handleCampaignSelect = (selection: Campaign | null) => {
    analytics?.trackCreateLinkCampaignDropdownEvent?.(
      selection ? selection.name : "none",
    );
    dispatch({ type: "SELECT_CAMPAIGN", campaign: selection });
  };

  const handleTargetPageConfirm = (selection: {
    offer: OfferLandingPages;
    landingPage: LandingPageLink;
  }) => {
    dispatch({
      type: "CONFIRM_TARGET_PAGE",
      offer: selection.offer,
      landingPage: selection.landingPage,
    });
  };

  const handleLanguageSelect = (option: LanguageSelectOption) => {
    const pick = selectedOffer?.landingPages?.find(
      (lp) => lp.languageCode === option.value,
    );
    if (!pick) return;
    dispatch({
      type: "SELECT_LANGUAGE",
      landingPage: pick,
    });

    analytics?.trackCreateLinkTargetPageLanguageSelectorEvent?.(option.label);
  };

  const handlePreviewLink = () => {
    if (!selectedLandingPage?.previewUrl) return;
    analytics?.trackCreateLinkPreviewLinkButtonEvent?.(linkName.trim());
    window.open(
      selectedLandingPage.previewUrl,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleCreateLink = async () => {
    analytics?.trackCreateLinkCreateButtonEvent?.({
      rebateProgram: selectedRebateProgram?.label,
      targetPage: selectedLandingPage?.name ?? "",
      language: selectedLanguage,
      name: linkName.trim(),
    });

    dispatch({ type: "SET_STATUS", status: "loading" });

    if (!selectedRebateProgram || !selectedOffer || !selectedLandingPage) {
      dispatch({ type: "SET_STATUS", status: "error" });
      return;
    }

    if (isDuplicateLinkName(linkName)) {
      dispatch({
        type: "SET_NAME_FIELD_STATUS",
        nameFieldStatus: "duplicateName",
      });
      dispatch({ type: "SET_STATUS", status: "idle" });
      return;
    }

    const isDuplicate = checkIfDuplicate(
      selectedOffer.id,
      selectedLandingPage.id,
      selectedLandingPage.languageCode,
    );

    if (isDuplicate) {
      dispatch({ type: "SET_STATUS", status: "duplicateName" });
      return;
    }

    const {
      languageCode: language,
      license,
      name: landingPageName,
    } = selectedLandingPage;
    const trimmedLinkName = linkName.trim();

    let resolvedCampaign: Campaign | undefined;
    try {
      resolvedCampaign = campaign
        ? await pendingCampaign.resolve(selectedCampaign)
        : undefined;
    } catch {
      dispatch({ type: "SET_STATUS", status: "idle" });
      return;
    }
    if (resolvedCampaign && resolvedCampaign !== selectedCampaign) {
      dispatch({ type: "SELECT_CAMPAIGN", campaign: resolvedCampaign });
    }

    try {
      const result = await actions.createReferralLink({
        HOID: selectedRebateProgram.hoid,
        landingPageName,
        language,
        license,
        linkType: "web",
        name: trimmedLinkName,
        offerID: selectedOffer.id,
        urlID: selectedLandingPage.id || undefined,
        campaignID: resolvedCampaign?.id,
      });

      if (result.referralLink && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(result.referralLink);
        } catch (err) {
          console.warn("Failed to copy referral link to clipboard:", err);
        }
      }
      showSuccessToast({ title: translations.successMessage });
      handleClose(true);
    } catch (error) {
      const is409 =
        error instanceof Error && error.message.includes("Status: 409");
      console.error("Error creating referral link:", error);
      dispatch({
        type: "SET_STATUS",
        status: is409 ? "duplicateName" : "error",
      });
    } finally {
      refetchReferralLinks();
    }
  };

  const handleClose = (refresh?: boolean) => {
    const firstRebateProgram = rebateProgramOptions[0];
    pendingCampaign.reset();
    dispatch({
      type: "RESET",
      defaultRebateProgram: firstRebateProgram ?? defaultRebateProgram,
    });
    onClose(refresh);
  };

  const isMasterSubIB = selectedRebateProgram?.isMasterSubIB ?? false;

  return (
    <>
      <ResponsiveModal
        isOpen={isOpen && !isTargetPageModalOpen}
        onClose={handleClose}
        isMobile={isMobile}
        maxWidth={512}
        title={translations.title}
        isDismissable={false}
      >
        {isGettingOfferIds || isGettingReferralLinks || campaign?.isLoading ? (
          <div className="flex flex-col gap-6">
            <CreateLinkFormSkeleton showCampaign={!!campaign} />
            <ModalFooterSkeleton isMobile={isMobile} />
          </div>
        ) : (hasError || campaign?.hasError) && status === "idle" ? (
          // A failed create sets status="error"/"duplicateName" and refetches
          // existing links in its `finally`; if that refetch also fails,
          // `hasError` flips true. Only show the full load-error state when the
          // modal is otherwise idle, so an inline create outcome (FieldError or
          // duplicate warning) is never swallowed by the refetch failure.
          // `campaign.hasError` reuses the same full-page state when the
          // campaign list itself fails to load.
          <LoadErrorState
            message={translations.loadErrorMessage}
            retryLabel={translations.loadErrorRetry}
            onRetry={() => {
              refetchInitialData();
              campaign?.onRetry?.();
            }}
          />
        ) : rebateProgramOptions.length === 0 && status === "idle" ? (
          <NoRebateProgramState
            title={translations.noRebateProgramTitle}
            message={translations.noRebateProgramMessage}
          />
        ) : (
          <div className="flex flex-col gap-6">
            {showRebateProgram && (
              <div className="flex flex-col gap-2">
                <RebateProgramSelect
                  options={rebateProgramOptions}
                  value={selectedRebateProgram ?? null}
                  onChange={handleRebateProgramChange}
                  translations={translations}
                />
                {isMasterSubIB && (
                  <div className="flex items-start gap-2">
                    <InfoIcon className="h-4 w-4 shrink-0 [&_path]:stroke-text-link-default" />
                    <Typo type="bodyXS" textColor="text-text-secondary">
                      {translations.rebateProgramMasterSubIBNotice}
                    </Typo>
                  </div>
                )}
              </div>
            )}

            {isGettingLandingPage ? (
              <TargetPageSelectorSkeleton>
                <div className="flex flex-col gap-1">
                  <Typo element="p" type="labelSM">
                    {translations.targetPageLabel}
                  </Typo>
                  <Typo type="bodyXS" textColor="text-text-secondary">
                    {translations.targetPageSubLabel}
                  </Typo>
                </div>
              </TargetPageSelectorSkeleton>
            ) : selectedOffer && selectedLandingPage ? (
              <TargetPageSelector
                offer={selectedOffer}
                landingPage={selectedLandingPage}
                selectedLanguage={selectedLanguage}
                languageOptions={languageOptions}
                onLanguageSelect={handleLanguageSelect}
                onOpenModal={() => {
                  analytics?.trackCreateLinkTargetPageSelectorEvent?.();
                  dispatch({ type: "OPEN_TARGET_PAGE_MODAL" });
                }}
                translations={{
                  targetPageLabel: translations.targetPageLabel,
                  targetPageSubLabel: translations.targetPageSubLabel,
                  previewLabel: translations.previewLabel,
                }}
              />
            ) : null}

            {campaign && (
              <div data-testid="campaign-field">
                <CampaignPicker
                  id="create-link-campaign"
                  label={campaign.label}
                  subLabel={campaign.subLabel}
                  value={selectedCampaign ?? null}
                  campaigns={pendingCampaign.campaigns}
                  isLoading={campaign.isLoading}
                  createError={pendingCampaign.createError}
                  onSelect={handleCampaignSelect}
                  onCreate={pendingCampaign.stage}
                  maxLength={LINK_NAME_MAX_LENGTH}
                />
              </div>
            )}

            <InputV2
              name="link-name"
              label={translations.name}
              labelType="labelSM"
              subLabel={translations.nameDescription}
              value={linkName}
              characterLimit={LINK_NAME_MAX_LENGTH}
              maxLength={LINK_NAME_MAX_LENGTH}
              onChange={(value) => {
                dispatch({ type: "SET_LINK_NAME", name: value });
                trackLinkNameInputDebounced(value);
              }}
              error={
                nameFieldStatus === "duplicateName"
                  ? translations.createLinkNameDuplicateError
                  : undefined
              }
            />

            {status === "error" && (
              <FieldError text={translations.errorMessage} />
            )}
            {status === "duplicateName" && (
              <FieldWarning
                text={translations.duplicateWarning}
                handleClose={handleClose}
                viewLinkText={translations.viewLinkLabel}
              />
            )}

            <div className="mt-2 flex flex-row items-center gap-4 lg:justify-end">
              <ButtonV2
                variant="outline"
                className="flex-1 lg:flex-none"
                onClick={handlePreviewLink}
              >
                <div className="flex items-center gap-2">
                  <ExternalLinkIcon className="h-4 w-4 [&_path]:stroke-icon-brand-default" />
                  {translations.previewLink}
                </div>
              </ButtonV2>
              <ButtonV2
                variant="brand"
                className="flex-1 lg:flex-none"
                onClick={handleCreateLink}
                isLoading={status === "loading"}
                disabled={
                  status === "loading" ||
                  status === "duplicateName" ||
                  nameFieldStatus === "duplicateName" ||
                  !selectedRebateProgram ||
                  !selectedOffer ||
                  isGettingLandingPage ||
                  !linkName.trim()
                }
              >
                {translations.confirmButton}
              </ButtonV2>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {selectedOffer && selectedLandingPage && (
        <TargetPageModal
          isOpen={isOpen && isTargetPageModalOpen}
          onClose={() => handleClose()}
          onBack={() => dispatch({ type: "CLOSE_TARGET_PAGE_MODAL" })}
          onConfirm={handleTargetPageConfirm}
          selectedOffer={selectedOffer}
          selectedLandingPage={selectedLandingPage}
          offerPages={landingPageOffers ?? []}
          translations={targetPageTranslations}
          getLanguageLabelFromCode={getLanguageLabelFromCode}
        />
      )}
    </>
  );
};
